/**
 * ProShell — iter87.79 Phase A · Clerk provider scoped to /rivalry-pro/*.
 *
 * Build-cache invalidator: iter87.199.85 (2026-02-24) — bumped to force
 * a fresh frontend build so the production Clerk publishable key
 * (`pk_live_...`) gets baked into the bundle instead of the stale
 * `pk_test_...` value the CDN was serving.
 *
 * BLAST-RADIUS BOUNDARY: <ClerkProvider> is mounted ONLY here, wrapping
 * the Rivalry Pro+ routes. Site Admin / Platform Admin / participant
 * surfaces MUST NEVER see Clerk state — they continue using local JWT
 * auth via `pickem_admin_user` localStorage.
 *
 * Design:
 * - When REACT_APP_CLERK_PUBLISHABLE_KEY is set, we mount ClerkProvider
 *   and render nested routes. Legacy /rivalry-pro/login remains as a
 *   fallback path (email+password) for existing Pro+ users who haven't
 *   migrated yet.
 * - When Clerk key is missing (e.g. dev misconfig), we render children
 *   without a provider — legacy flow keeps working.
 */
import { ClerkProvider, SignIn, SignUp } from "@clerk/react";

/**
 * iter87.199.105 → iter87.199.130 — Shared Clerk appearance theme.
 *
 * Applied at the <ClerkProvider> boundary so every Clerk widget we
 * host — <SignIn>, <SignUp>, <UserButton>, <UserProfile> — inherits
 * ONE consistent theme (operator feedback: surfaces were rendering in
 * different colors depending on which widget was open).
 *
 * iter87.199.107 reverted from a dark canvas to a light one because
 * <SignIn>/<SignUp> rendered with unreadable text — that happened
 * because only `colorBackground` was flipped dark without also
 * pinning every text-bearing variable/element to an explicit, always-
 * readable value, so Clerk's automatic contrast calculation guessed
 * wrong for some sub-elements. iter87.199.129 hit the exact same trap
 * scoped to just <UserButton>'s popover (the account name/email line
 * went near-invisible).
 *
 * iter87.199.130 goes dark again, matching the app's own bento-card
 * language (`rounded-2xl border border-white/[0.08] bg-[#121212]`,
 * see e.g. `BentoHome.jsx`) — but this time every text, input, and
 * button element Clerk exposes gets an EXPLICIT className color, not
 * just the `variables` block, so nothing is left to automatic
 * contrast guessing. `<SignIn>`/`<SignUp>` are screenshot-verified
 * (public routes); `<UserButton>` popover + `<UserProfile>` modal
 * are not (no test account in this environment) — same theme
 * mechanism, but flag if anything still reads wrong.
 *
 * Cyber-yellow stays the accent for links/icons (explicit `text-cyber`
 * overrides below). iter87.404 — the primary CTA button (`CONTINUE`
 * on <SignIn>/<SignUp>, the main action on <UserProfile>) switched to
 * volt-blue via `colorPrimary` + `formButtonPrimary`, matching the
 * app's own `brutal-btn-secondary` button style elsewhere.
 *
 * ⚠️ This does NOT theme the Clerk-hosted Account Portal on
 * `accounts.<instance>.dev` — configured separately in Clerk
 * Dashboard → Customization → Appearance.
 */
const TCR_CLERK_APPEARANCE = {
    variables: {
        colorPrimary: "#007AFF",              // volt-blue (iter87.404 — was cyber-yellow)
        colorTextOnPrimaryBackground: "#ffffff",
        colorBackground: "#121212",           // bento dark canvas
        colorText: "#f4f4f5",
        colorInputBackground: "#1a1a1a",
        colorInputText: "#f4f4f5",
        colorTextSecondary: "#a1a1aa",        // zinc-400, readable on dark
        colorNeutral: "#f4f4f5",
        borderRadius: "1rem",                 // ~ rounded-2xl, matches bento cards
        fontFamily: "inherit",
    },
    elements: {
        // Modal outermost wrappers (<UserProfile>) + <SignIn>/<SignUp> card.
        rootBox: "bg-[#121212]",
        modalContent: "bg-[#121212]",
        cardBox: "bg-[#121212] border border-white/[0.08] rounded-2xl",
        card: "bg-[#121212] border border-white/[0.08] rounded-2xl shadow-none",
        headerTitle: "text-white",
        headerSubtitle: "text-zinc-400",
        socialButtonsBlockButton: "border border-white/[0.08] bg-[#1a1a1a] text-white hover:bg-[#1f1f1f]",
        socialButtonsBlockButtonText: "text-white font-medium",
        dividerLine: "bg-white/[0.08]",
        dividerText: "text-zinc-500",
        formFieldLabel: "text-zinc-300",
        formFieldInput: "bg-[#1a1a1a] border border-white/[0.08] text-white",
        formFieldAction: "text-cyber",
        footerActionText: "text-zinc-400",
        identityPreviewText: "text-zinc-300",
        identityPreviewEditButton: "text-cyber",
        navbar: "bg-[#121212]",
        navbarButton: "text-zinc-300",
        profileSectionTitleText: "text-white",
        // iter87.199.135 — <UserProfile> "Manage account" modal (Profile +
        // Security tabs) had zero overrides here, so its own nested rows
        // (email/connected-account list text, active-device name/browser/
        // IP/timestamp, the "Primary"/"This device" pills, per-row "..."
        // menus) fell back to Clerk's automatic contrast guess against the
        // dark canvas and rendered near-invisible — same trap as
        // iter87.199.107/.129, now hitting the one surface those passes
        // didn't reach (no test account existed to catch it earlier).
        profileSectionContent: "text-zinc-300",
        profileSectionPrimaryButton: "text-cyber",
        badge: "bg-white/[0.08] text-zinc-200 border border-white/[0.12]",
        menuButton: "text-zinc-300 hover:bg-white/[0.05]",
        activeDevice: "text-zinc-300",
        activeDeviceIcon: "text-zinc-400",
        tableHead: "text-zinc-400",
        accordionTriggerButton: "text-zinc-200",
        // iter87.199.138 — Account/Security page nav list rows (email
        // addresses list, connected accounts list) + the page's own
        // "Account" / "Security" title+subtitle header, all invisible
        // against the dark canvas until pinned explicitly.
        accountListItem: "text-zinc-200",
        profilePage: "text-zinc-200",
        pageScrollBox: "bg-[#121212] text-zinc-200",
        // Brand accents on CTA + link.
        formButtonPrimary:
            "bg-volt hover:bg-volt/90 text-white font-black uppercase tracking-wider",
        footerActionLink: "text-cyber hover:text-cyber/80 font-bold",
        // <UserButton> avatar trigger + its popover — kept flush/dark
        // with the app header instead of Clerk's default white circle.
        userButtonAvatarBox: "w-7 h-7 rounded-full ring-1 ring-white/[0.15]",
        userButtonBox: "bg-transparent",
        userButtonTrigger: "bg-transparent focus:shadow-none rounded-full",
        userButtonPopoverCard: "bg-[#121212] border border-white/[0.08] rounded-2xl shadow-xl",
        userButtonPopoverMain: "bg-[#121212]",
        userButtonPopoverActionButton: "text-zinc-200 hover:bg-white/[0.05]",
        userButtonPopoverActionButtonText: "text-zinc-200",
        userButtonPopoverActionButtonIcon: "text-cyber",
        userButtonPopoverFooter: "bg-[#121212] border-t border-white/[0.08]",
        userPreviewMainIdentifier: "text-white font-semibold",
        userPreviewSecondaryIdentifier: "text-zinc-400",
        userPreviewAvatarBox: "rounded-full",
        avatarBox: "rounded-full",
        avatarImage: "rounded-full",
    },
};
import { useAuth } from "@clerk/react";
import { Outlet, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { configureClerkTokenGetter } from "./api";
import FirstTimeConsentModal from "@/components/FirstTimeConsentModal";
import ProPlusBillingTermsModal from "@/components/ProPlusBillingTermsModal";
import TermsModal from "@/components/TermsModal";
import PrivacyModal from "@/components/PrivacyModal";
import { hasPlatformConsent, recordPlatformConsent } from "@/lib/platformConsent";
import ProPlusHomeScreenTags from "./ProPlusHomeScreenTags";

const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const SIGN_IN_URL = process.env.REACT_APP_CLERK_SIGN_IN_URL || "/rivalry-pro/sign-in";
const SIGN_UP_URL = process.env.REACT_APP_CLERK_SIGN_UP_URL || "/rivalry-pro/sign-up";
const AFTER_SIGN_IN = "/rivalry-pro";
const AFTER_SIGN_UP = "/rivalry-pro";

/**
 * ClerkTokenBridge — iter87.199.69
 *
 * Binds Clerk's `getToken` into the `proApi` axios client for EVERY
 * child route of ProShell, not just the RivalryProApp index. Before
 * this fix the getter was only wired inside `RivalryProApp.jsx`, so
 * a user landing directly on `/rivalry-pro/new` (the new onboarding
 * route) had no Clerk bearer attached — every `proApi.createLeague`
 * call failed with 401 "Missing Pro+ bearer token".
 *
 * Rendering this as a sibling of `<Outlet />` keeps the useAuth()
 * hook alive for the lifetime of ALL rivalry-pro routes.
 */
function ClerkTokenBridge() {
    const { isSignedIn, getToken } = useAuth();
    useEffect(() => {
        if (isSignedIn && typeof getToken === "function") {
            configureClerkTokenGetter(() => getToken());
            return () => configureClerkTokenGetter(null);
        }
        configureClerkTokenGetter(null);
        return undefined;
    }, [isSignedIn, getToken]);
    return null;
}

export function ProShell() {
    const navigate = useNavigate();

    if (!PUBLISHABLE_KEY) {
        // Legacy-only mode — Clerk not configured. Render nested route
        // as if the provider were transparent.
        return (
            <>
                <ProPlusHomeScreenTags />
                <Outlet />
                <ProShellFooter />
            </>
        );
    }

    return (
        <ClerkProvider
            publishableKey={PUBLISHABLE_KEY}
            appearance={TCR_CLERK_APPEARANCE}
            routerPush={(to) => navigate(to)}
            routerReplace={(to) => navigate(to, { replace: true })}
            signInUrl={SIGN_IN_URL}
            signUpUrl={SIGN_UP_URL}
            signInFallbackRedirectUrl={AFTER_SIGN_IN}
            signUpFallbackRedirectUrl={AFTER_SIGN_UP}
            afterSignOutUrl="/rivalry-pro/login"
        >
            <ProPlusHomeScreenTags />
            <ClerkTokenBridge />
            <Outlet />
            <ProShellFooter />
        </ClerkProvider>
    );
}

/**
 * iter87.199.111 — Legal footer for the Pro+ shell. Renders on every
 * /rivalry-pro/* route (marketing landing, hub, sign-in, sign-up,
 * account sub-routes) so the Billing Terms — which include the
 * refund policy at §4 — are always one tap away for paid users.
 *
 * The general TCR marketing landing (/) is intentionally NOT wrapped
 * in this footer; billing terms only apply to Pro+ tier and would
 * confuse free-tier visitors.
 */
function ProShellFooter() {
    const [billingTermsOpen, setBillingTermsOpen] = useState(false); // iter87.199.145 — in-flow terms modal
    const [termsOpen, setTermsOpen] = useState(false); // iter87.199.146 — consistency: all 3 legal links now open modals
    const [privacyOpen, setPrivacyOpen] = useState(false);
    return (
        <footer
            className="border-t border-white/10 bg-[#0a0a0a] text-white/60 text-[11px] px-4 py-6 mt-8"
            data-testid="rivalry-pro-footer"
        >
            <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <button
                        type="button"
                        onClick={() => setTermsOpen(true)}
                        className="hover:text-cyber transition-colors"
                        data-testid="rivalry-pro-footer-terms"
                    >
                        Terms of Service
                    </button>
                    <span className="text-white/20">·</span>
                    <button
                        type="button"
                        onClick={() => setPrivacyOpen(true)}
                        className="hover:text-cyber transition-colors"
                        data-testid="rivalry-pro-footer-privacy"
                    >
                        Privacy
                    </button>
                    <span className="text-white/20">·</span>
                    <button
                        type="button"
                        onClick={() => setBillingTermsOpen(true)}
                        className="hover:text-cyber transition-colors"
                        data-testid="rivalry-pro-footer-billing-terms"
                    >
                        Pro+ Billing Terms & Refund Policy
                    </button>
                </div>
                <div className="text-white/40" data-testid="rivalry-pro-footer-copyright">
                    © Tiny Crew Rivalries
                </div>
            </div>
            <ProPlusBillingTermsModal open={billingTermsOpen} onClose={() => setBillingTermsOpen(false)} />
            <TermsModal
                open={termsOpen}
                onClose={() => setTermsOpen(false)}
                onAccept={() => setTermsOpen(false)}
                onShowPrivacy={() => { setTermsOpen(false); setPrivacyOpen(true); }}
                viewOnly
            />
            <PrivacyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
        </footer>
    );
}

/** Clerk-hosted sign-in card, mounted at /rivalry-pro/sign-in. */
export function ProClerkSignIn() {
    // iter87.281 — Pro+ grant emails link here with ?email=... so the
    // recipient doesn't have to retype it. Pure prefill only — NOT
    // verification/proof of ownership, Clerk's own auth still runs
    // normally on top of it.
    const emailPrefill = new URLSearchParams(window.location.search).get("email") || undefined;
    return (
        <div
            className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4"
            data-testid="rivalry-pro-clerk-signin-shell"
        >
            <div className="w-full max-w-md">
                <SignIn
                    routing="hash"
                    fallbackRedirectUrl="/rivalry-pro"
                    signUpUrl="/rivalry-pro/sign-up"
                    signUpFallbackRedirectUrl="/rivalry-pro"
                    initialValues={emailPrefill ? { emailAddress: emailPrefill } : undefined}
                />
            </div>
        </div>
    );
}

/** Clerk-hosted sign-up card, mounted at /rivalry-pro/sign-up.
 *
 * iter87.148 — Required companion to `<SignIn />` so first-time Google
 * OAuth users have a landing surface for the sign-up completion step.
 * Without this route, clicking "Continue with Google" on the sign-in
 * widget as a brand-new user fails with "The External Account was not
 * found" (Clerk can't route the OAuth callback to a sign-up completion
 * component). Renders email + Google (both, per operator direction) so
 * users without a Google account can still self-serve register. All
 * new users still land in the queued-for-approval state on
 * `/rivalry-pro` — Pro+ entitlement is granted by the site admin via
 * the existing Pro+ Subscriptions tab.
 */
export function ProClerkSignUp() {
    // iter87.199.97 — Age 18+ + TCR platform T&C gate BEFORE the Clerk
    // sign-up widget renders. Reuses the shared FirstTimeConsentModal
    // (age checkbox + T&C checkbox). Persisted in localStorage so
    // returning visitors aren't re-prompted every time. Note: LS
    // billing has its OWN Pro+ Billing Terms acceptance gate downstream
    // at the Subscribe step (see SubscribeProPlus.jsx / iter87.199.97).
    const [consentOk, setConsentOk] = React.useState(() => hasPlatformConsent());
    const [declined, setDeclined] = React.useState(false);
    // iter87.281 — Pro+ grant emails link here with ?email=... so the
    // recipient doesn't have to retype it. Pure prefill only.
    const emailPrefill = new URLSearchParams(window.location.search).get("email") || undefined;

    const onAccept = () => {
        recordPlatformConsent();
        setConsentOk(true);
    };
    const onDecline = () => setDeclined(true);

    if (declined) {
        return (
            <div
                className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4"
                data-testid="rivalry-pro-clerk-signup-declined"
            >
                <div className="brutal-card max-w-md w-full p-6 space-y-3 text-center">
                    <h1 className="font-display font-black text-xl tracking-tight">Can't continue</h1>
                    <p className="text-sm text-zinc-300">
                        You must be 18+ and agree to the Terms & Privacy Policy to use Tiny Crew Rivalries Pro+.
                    </p>
                    <button
                        type="button" onClick={() => setDeclined(false)}
                        data-testid="rivalry-pro-clerk-signup-reopen-consent"
                        className="brutal-btn text-xs py-2 w-full"
                    >
                        Review again
                    </button>
                    <div className="grid grid-cols-2 gap-2">
                        <a
                            href="/rivalry-pro"
                            data-testid="rivalry-pro-clerk-signup-declined-back-proplus"
                            className="brutal-btn-ghost text-xs py-2"
                        >
                            Back to Pro+
                        </a>
                        <a
                            href="/"
                            data-testid="rivalry-pro-clerk-signup-declined-main-site"
                            className="brutal-btn-ghost text-xs py-2"
                        >
                            Main site
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4"
            data-testid="rivalry-pro-clerk-signup-shell"
        >
            <FirstTimeConsentModal
                open={!consentOk}
                onAccept={onAccept}
                onDecline={onDecline}
                acceptLabel="Continue sign-up"
                submittingLabel="Continuing…"
            />
            {consentOk && (
                <div className="w-full max-w-md">
                    <SignUp
                        routing="hash"
                        fallbackRedirectUrl="/rivalry-pro"
                        signInUrl="/rivalry-pro/sign-in"
                        signInFallbackRedirectUrl="/rivalry-pro"
                        initialValues={emailPrefill ? { emailAddress: emailPrefill } : undefined}
                    />
                </div>
            )}
        </div>
    );
}
